import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { appConfig } from '../config/appConfig';

const Header = () => {
  const { logout, user, hasPerm } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const logoSrc = require(`../assets/${appConfig.logo || 'encore.jpg'}`);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const dynamicNav = [
    appConfig.showUnsignedPage && hasPerm('unsigned') && { path: '/unsigned', label: 'Unsigned' },
    appConfig.showScheduler && hasPerm('scheduler') && { path: '/scheduler', label: 'Scheduler' },
    // Snoop, Luminate and Deals all live inside the Unsigned page's source
    // selector, matching how the coworker dashboard groups its "unsigned"
    // suite. The mapping tables that drive those feeds get their own tab.
    (appConfig.showSnoop || appConfig.showLuminate) &&
      user?.isAdmin && { path: '/mapping/snoop', label: 'Mapping' },
    // PGP publishing suite; its own sub-tabs live inside the page. Labelled
    // "Unsigned" to match how the Encore site names its scouting suite — there
    // is no clash, since the /unsigned tab is Encore-build only.
    appConfig.showPgpSuite && hasPerm('pgp') && { path: '/pgp/agenda', label: 'Unsigned' },
    // Operations suite; its own sub-tabs live inside the page.
    appConfig.showOperations && hasPerm('operations') && {
      path: '/operations/assets',
      label: 'Operations',
    },
    user?.isAdmin && { path: '/admin', label: 'Admin' },
  ].filter(Boolean);

  const navItems = [...appConfig.navItems, ...dynamicNav];

  const linkClass = (path) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      location.pathname === path
        ? 'text-red-600 bg-red-50'
        : 'text-gray-900 hover:text-red-600'
    }`;

  return (
    <header className="bg-white shadow">
      <div className="max-w-9xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src={logoSrc}
              alt={`${appConfig.appName} Logo`}
              className="h-12 w-auto sm:h-16 md:h-20 transition-transform hover:scale-105"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center space-x-2 lg:space-x-4">
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} className={linkClass(item.path)}>
                {item.label}
              </Link>
            ))}
            {user?.email && (
              <span className="text-gray-500 text-sm hidden lg:inline">{user.email}</span>
            )}
            <button
              onClick={handleLogout}
              className="text-gray-900 hover:text-red-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Logout
            </button>
          </nav>

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-red-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile dropdown */}
        {menuOpen && (
          <nav className="md:hidden mt-2 pb-2 border-t border-gray-200 pt-2 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block ${linkClass(item.path)}`}
              >
                {item.label}
              </Link>
            ))}
            {user?.email && (
              <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-100 mt-2 pt-3">
                Signed in as {user.email}
              </div>
            )}
            <button
              onClick={handleLogout}
              className="block w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-900 hover:text-red-600 hover:bg-gray-50"
            >
              Logout
            </button>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
